import { useState } from 'react';
import { MiniFloorplanMap } from '../legacy-ball/table-management/MiniFloorplanMap';

export interface TableGuestInfo {
  fullName: string;
  abbreviatedName: string;
  seatNumber: number;
  isCurrentUser: boolean;
  mealSelection?: string | null;
  orderReferenceId?: string | null;
  tableRequestGroupId?: string | null;
}

export interface SeatInfo {
  tableNumber: number;
  seatNumber: number;
  tableName?: string;
  zoneName?: string;
  entryGuidance?: string;
  wayfindingInstructions?: string;
  gridAddress?: string;
  suggestedEntrance?: string;
  tableGuests?: TableGuestInfo[];
}

export interface SeatingCardProps {
  seat: SeatInfo | null;
  userRole?: string;
  userOrderRef?: string | null;
  showTableGuestNames?: boolean;
}

function getInitials(name: string): string {
  return name.trim().split(/\s+/).map((p) => p.charAt(0).toUpperCase()).join('');
}

export function SeatingCard({ seat, userRole, userOrderRef, showTableGuestNames = true }: SeatingCardProps) {
  const [showGuests, setShowGuests] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const guestCount = seat?.tableGuests?.length || 0;

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        padding: '1.25rem',
      }}
    >
      <div
        style={{
          fontSize: '0.85rem',
          fontWeight: 600,
          color: '#1a6b3c',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '0.75rem',
        }}
      >
        Seating Assignment
      </div>

      {seat ? (
        <div>
          {(seat.tableName || seat.zoneName) && (
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
              {seat.tableName || ''}
              {seat.zoneName && <span style={{ marginLeft: seat.tableName ? '8px' : '0', fontSize: '0.75rem', color: '#6b7280' }}>({seat.zoneName} Zone)</span>}
            </div>
          )}
          {seat.wayfindingInstructions && (
            <div style={{ fontSize: '0.8rem', color: '#1e40af', marginBottom: '0.6rem', display: 'flex', alignItems: 'flex-start', gap: '6px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '0.5rem 0.75rem', lineHeight: 1.5 }}>
              <span style={{ flexShrink: 0 }}>🧭</span>
              <span>{seat.wayfindingInstructions}</span>
            </div>
          )}
          {!seat.wayfindingInstructions && seat.entryGuidance && (
            <div style={{ fontSize: '0.8rem', color: '#374151', marginBottom: '0.6rem', display: 'flex', alignItems: 'flex-start', gap: '6px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
              <span style={{ flexShrink: 0 }}>🚪</span>
              <span>{seat.entryGuidance}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '0.5rem 1rem', minWidth: '64px' }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Table</span>
              <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f2d1f', lineHeight: 1.1 }}>{seat.tableNumber}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '8px', padding: '0.5rem 1rem', minWidth: '64px' }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Seat</span>
              <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e3a5f', lineHeight: 1.1 }}>{seat.seatNumber}</span>
            </div>
            {guestCount > 0 && (
              <button
                onClick={() => setShowGuests(!showGuests)}
                style={{
                  marginLeft: 'auto',
                  background: showGuests ? '#f0fdf4' : '#f9fafb',
                  border: `1px solid ${showGuests ? '#86efac' : '#d1d5db'}`,
                  borderRadius: '8px',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: showGuests ? '#166534' : '#374151',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.15s ease',
                }}
              >
                👥 Table Guests ({guestCount})
              </button>
            )}
          </div>

          {/* View Seat Map button */}
          {seat.gridAddress && (
            <div style={{ marginTop: '0.6rem' }}>
              <button
                onClick={() => setShowMap(!showMap)}
                style={{
                  background: showMap ? '#1a1a2e' : '#f9fafb',
                  border: `1px solid ${showMap ? '#4ade80' : '#d1d5db'}`,
                  borderRadius: '8px',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: showMap ? '#4ade80' : '#374151',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.15s ease',
                }}
                aria-expanded={showMap}
                aria-label="Show seat on venue map"
              >
                🗺️ {showMap ? 'Hide Map' : 'View Seat Map'}
              </button>
              {showMap && (
                <div style={{ marginTop: '0.6rem', display: 'flex', justifyContent: 'center' }}>
                  <MiniFloorplanMap highlightedGridAddress={seat.gridAddress} suggestedEntrance={seat.suggestedEntrance} />
                </div>
              )}
            </div>
          )}

          {showGuests && seat.tableGuests && seat.tableGuests.length > 0 && (
            <div
              style={{
                marginTop: '0.75rem',
                borderTop: '1px solid #e5e7eb',
                paddingTop: '0.75rem',
              }}
            >
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                Table Guests
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {seat.tableGuests.map((guest, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.6rem 0.75rem',
                      backgroundColor: guest.isCurrentUser ? '#f0fdf4' : '#fff',
                      border: guest.isCurrentUser ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      marginBottom: idx < seat.tableGuests!.length - 1 ? '0.4rem' : 0,
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontSize: '0.85rem',
                          color: guest.isCurrentUser ? '#166534' : '#0f2d1f',
                          fontWeight: guest.isCurrentUser ? 700 : 500,
                        }}
                      >
                        {(() => {
                          // Current user always sees their own name
                          if (guest.isCurrentUser) {
                            return guest.abbreviatedName || guest.fullName;
                          }
                          // When admin has disabled guest name visibility:
                          // same order or same table request group → show name; otherwise → "Occupied"
                          if (!showTableGuestNames) {
                            const sameOrder = userOrderRef && guest.orderReferenceId && guest.orderReferenceId === userOrderRef;
                            const sameGroup = (() => {
                              if (!guest.tableRequestGroupId) return false;
                              // Check if current user shares a table request group with this guest
                              const currentUserEntry = seat?.tableGuests?.find(g => g.isCurrentUser);
                              return currentUserEntry?.tableRequestGroupId === guest.tableRequestGroupId;
                            })();
                            return (sameOrder || sameGroup) ? (guest.abbreviatedName || guest.fullName) : 'Occupied';
                          }
                          // Normal mode: Attendees see initials for guests on a different order
                          const isAttendee = userRole === 'Attendee';
                          if (!isAttendee) {
                            return guest.abbreviatedName || guest.fullName;
                          }
                          // If either order ref is unknown, show abbreviated name (benefit of doubt)
                          if (!userOrderRef || !guest.orderReferenceId) {
                            return guest.abbreviatedName || guest.fullName;
                          }
                          // Same order → show name; different order → initials
                          if (guest.orderReferenceId === userOrderRef) {
                            return guest.abbreviatedName || guest.fullName;
                          }
                          return getInitials(guest.fullName);
                        })()}
                      </span>
                      {guest.isCurrentUser && (
                        <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: '#16a34a', fontWeight: 600 }}>
                          (You)
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {(() => {
                        const meal = guest.mealSelection;
                        const isSalmon = meal?.toLowerCase().includes('salmon');
                        const isChicken = meal?.toLowerCase().includes('chicken');
                        const icon = isSalmon ? '🐟' : isChicken ? '🍗' : '🍽️';
                        const bg = isSalmon ? '#60a5fa' : isChicken ? '#fb923c' : '#d1d5db';
                        const border = isSalmon ? '#2563eb' : isChicken ? '#ea580c' : '#6b7280';
                        return (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              backgroundColor: bg,
                              border: `1px solid ${border}`,
                              minWidth: '28px',
                            }}
                            title={meal || 'No meal selected'}
                          >
                            {icon}
                          </span>
                        );
                      })()}
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '10px' }}>
                        Seat {guest.seatNumber}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            fontSize: '0.95rem',
            color: '#c9a84c',
            fontWeight: 500,
          }}
        >
          Pending
        </div>
      )}
    </div>
  );
}
